import {selx} from 'clsx';
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {
  isBadRequestError,
  isDataClassificationError,
  isHPCListNotSupported,
  isInvalidFormulasError,
  isMissingDataGroupError,
  isUnsupportedActionError,
  isUnsupportedAttributionError,
  trackMixpanelTips,
} from 'common/report/queries/utils';
import {getMissingDatagroupMappedProperty} from 'common/report/util/properties';
import {SpinnerEl} from 'common/components/spinner';
import {SpinnerSize, SpinnerTheme} from 'common/components/spinner/types';
import {QuerySamplingCtaEl} from 'common/query-builder/query-sampling-cta';
import {TooltipEl} from 'common/components/tooltip';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {BaseQueryErrorType} from 'common/report/types';
import {WarningTheme} from 'common/components/warning/constants';

import {
  EMPTY_REPORT_HEADER,
  NoDataReason,
  ReportStatus,
  TemplateEntryPointPath,
  TIPS,
  WarningHeroReportName,
  WarningHeroTheme,
  WarningTrackingError,
} from './constants';
import {WarningHeroEl} from './index';
import styles from './presets.module.styl';

export class TipFactory {
  static index = -1;
  static startEpoch = 0;

  static getTip() {
    if (TipFactory.index === -1) {
      TipFactory.index = Math.floor(Math.random() * TIPS.length);
    }

    return TIPS[TipFactory.index];
  }

  static resetTip() {
    TipFactory.index = -1;
    TipFactory.startEpoch = 0;
  }
}

export function TipsHero() {
  const tip = TipFactory.getTip();

  return (
    <WarningHeroEl
      attrs={{
        theme: WarningTheme.Tip,
        'header-text': tip.title || ``,
        'body-text': tip.body || ``,
        'margin-override': `true`,
      }}
      hook={{
        insert: () => {
          trackMixpanelTips(TipFactory.index);
          TipFactory.startEpoch = Date.now();
        },
        destroy: () => {
          trackMixpanelTips(TipFactory.index, Date.now() - TipFactory.startEpoch);
          TipFactory.resetTip();
        },
      }}
    />
  );
}

export function LoadingHero(params: {bodyText?: string; headerText?: string} = {}) {
  const {headerText, bodyText} = params || {};
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Loading,
        'header-text': headerText || ``,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function SensitiveDataHero({bodyText, reportName}: {bodyText?: string; reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Sensitive,
        report: reportName,
        'header-text': `Report contains classified data`,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function UnsupportedActionHero({bodyText, reportName}: {bodyText: string; reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        'header-text': `Unsupported Request`,
        'body-text': bodyText,
        report: reportName,
        'tracking-error': WarningTrackingError.UnsupportedActionError,
      }}
    />
  );
}

export function MissingDataGroupMappingHero({
  property,
  reportName,
  marginOverride = ``,
}: {
  property: string;
  reportName: WarningHeroReportName;
  marginOverride?: string;
}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        'header-text': `Unable to Display Data`,
        'body-text': `The property "${property}" is no longer available for use. \n Please use another property or contact your project admin`,
        report: reportName,
        'tracking-error': WarningTrackingError.MissingMappedDatagroup,
        'margin-override': marginOverride,
      }}
    />
  );
}

export function ServerErrorHero({
  headerText,
  bodyText,
  reportName,
}: {
  headerText?: string;
  bodyText?: string;
  reportName: WarningHeroReportName;
}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        report: reportName,
        'header-text': headerText || `Oops, something seems to have gone wrong. Please try again.`,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function NoDataHero({
  bodyText,
  reportName,
  reason,
}: {
  bodyText?: string;
  reason?: NoDataReason;
  reportName: WarningHeroReportName;
}) {
  let headerText = `We couldn't find any data for your query.`;

  if (reason === NoDataReason.EmptyProject) {
    headerText = `We don't have any data for your project.`;
  } else if (reason === NoDataReason.Timeout) {
    headerText = `Sorry, the query timed out.`;
  }

  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.NoData,
        'header-text': headerText,
        report: reportName,
        'body-text': bodyText || ``,
      }}
    />
  );
}

export function EmptyDashboardAddReportHeroTextCards(_props = null, childrenActions) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': `Add a new chart, text or use a template to get started.`,
        report: WarningHeroReportName.Dashboards,
      }}
    >
      {childrenActions}
    </WarningHeroEl>
  );
}

export function EmptyDashboardCreateHero({
  onCreateDashboardClick,
  onOpenTemplateModalClick,
  onOpenFoundationalTemplateModalClicked,
  shouldUseFoundationalTemplates,
  headerText = `Create a board to monitor your metrics in one place.`,
  bodyText = ``,
}) {
  const createDashboardButtonAttrs = {
    elref: `emptyCreateDashboardButton`,
    theme: ButtonTheme.Primary,
  };
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': headerText,
        'body-text': bodyText,
        report: WarningHeroReportName.Dashboards,
      }}
    >
      <div>
        <ButtonEl attrs={createDashboardButtonAttrs} on={{click: onCreateDashboardClick}}>
          New Board
          <TooltipEl
            attrs={{
              placement: Placement.Bottom,
              size: DisplaySize.Medium,
              text: `**New Board** generates a blank board so you can start building from scratch.`,
            }}
          />
        </ButtonEl>
        {!shouldUseFoundationalTemplates ? (
          <ButtonEl
            sel='.kpi-template-add-button'
            style={{'margin-left': `10px`}}
            attrs={{elref: `kpiTemplateAddButton`, theme: ButtonTheme.Secondary}}
            on={{click: () => onOpenTemplateModalClick({entryPoint: TemplateEntryPointPath.FirstDashboard})}}
          >
            Use a Template
            <TooltipEl
              attrs={{
                placement: Placement.Bottom,
                size: DisplaySize.Medium,
                text: `**Use a Template** to add your events and Mixpanel will generate a starter board for you. This feature is currently in beta.`,
              }}
            />
          </ButtonEl>
        ) : (
          <ButtonEl
            sel='.foundational-template-add-button'
            style={{'margin-left': `10px`}}
            attrs={{elref: `foundationalTemplateAddButton`, theme: ButtonTheme.Secondary}}
            on={{
              click: () => onOpenFoundationalTemplateModalClicked({entryPoint: TemplateEntryPointPath.FirstDashboard}),
            }}
          >
            Use a Template
          </ButtonEl>
        )}
      </div>
    </WarningHeroEl>
  );
}

export function EmptyLookupTableHero({onImportLookupTableClick}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': EMPTY_REPORT_HEADER[WarningHeroReportName.LookupTables],
        report: WarningHeroReportName.LookupTables,
      }}
    >
      <ButtonEl
        attrs={{
          icon: `plus`,
          theme: ButtonTheme.Primary,
        }}
        on={{click: onImportLookupTableClick}}
      >
        Import Lookup Table
      </ButtonEl>
    </WarningHeroEl>
  );
}

export function EmptyReportHero({reportName}: {reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.EmptyReport,
        'header-text': EMPTY_REPORT_HEADER[reportName],
        report: reportName,
      }}
    />
  );
}

export function InvalidFormulasHero({reportName}: {reportName: WarningHeroReportName}) {
  return (
    <WarningHeroEl
      attrs={{
        theme: WarningHeroTheme.Error,
        'header-text': `One or more of the formulas are invalid`,
        report: reportName,
      }}
    />
  );
}

const CONTACT_SUPPORT_MESSAGE = `If this issue persists, please [contact support](https://mixpanel.com/get-support).`;
// TODO(@Gabe): Use in Retention/Flows/Insights
export function ReportStatusHero({
  reportName,
  queryError,
  reportStatus,
  showQuerySamplingCTA,
  showMpTips,
  enableQuerySampling,
}: {
  reportName: WarningHeroReportName;
  queryError?: BaseQueryErrorType;
  reportStatus?: ReportStatus;
  showQuerySamplingCTA?: boolean;
  showMpTips?: boolean;
  enableQuerySampling?: (ev) => void;
}) {
  if (queryError) {
    const {error, status, data} = queryError;
    if (isDataClassificationError(error) || status === 423) {
      return <SensitiveDataHero bodyText={error} reportName={reportName} />;
    } else if (isMissingDataGroupError(error)) {
      const property = getMissingDatagroupMappedProperty(data);
      return <MissingDataGroupMappingHero property={property} reportName={reportName} />;
    } else if (isUnsupportedActionError(status)) {
      return <UnsupportedActionHero bodyText={error} reportName={reportName} />;
    } else if (isInvalidFormulasError(error)) {
      return <InvalidFormulasHero reportName={reportName} />;
    } else if (isUnsupportedAttributionError(error)) {
      return <UnsupportedActionHero bodyText={error} reportName={reportName} />;
    } else if (isHPCListNotSupported(error)) {
      return <UnsupportedActionHero bodyText={`Cannot hold List Property constant.`} reportName={reportName} />;
    } else if (isBadRequestError(status)) {
      return (
        <ServerErrorHero
          headerText='The client has issued a malformed request.'
          bodyText={CONTACT_SUPPORT_MESSAGE}
          reportName={reportName}
        />
      );
    } else {
      return <ServerErrorHero bodyText={CONTACT_SUPPORT_MESSAGE} reportName={reportName} />;
    }
  }

  switch (reportStatus) {
    case ReportStatus.Empty:
      return <EmptyReportHero reportName={reportName} />;
    case ReportStatus.NoData:
      return <NoDataHero bodyText={`We couldn't find any data for that date range.`} reportName={reportName} />;
    case ReportStatus.Loading:
      return (
        <div sel={selx(styles.loadingState)}>
          {/* IMPORTANT: do not change .chart-loader selector unless modifying screenshot_service/report_screenshot.js first */}
          <SpinnerEl
            sel='.chart-loader'
            attrs={{size: SpinnerSize.Medium, elref: `chartLoader`, 'allow-festive': true, theme: SpinnerTheme.Fancy}}
          />
          <div sel='tip-container' style={{height: `120px`}}>
            {showQuerySamplingCTA ? (
              <QuerySamplingCtaEl attrs={{slot: `loading`}} on={{enable: enableQuerySampling}} />
            ) : showMpTips ? (
              <TipsHero />
            ) : null}
          </div>
        </div>
      );
    default:
      return null;
  }
}
